// S34 權益贈與(/plan-grants)— Customer Plan/Grant(IA specs/01 §2.3)。
// 以 subject_key(type:id)查訂閱權益來源、建立限期/永久 grant、撤銷 grant
// (reason/case 必填)。「來源鏈 explain」= 商業層(Plan/grant)前端展開 +
// 參考合成;裝置對象另讀 server-authoritative effective resolve，不由 UI 自算。
// grant 建立/撤銷均走 durable maker/checker + password step-up。

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './commerce.css';
import type { EffectiveEntitlementExplain, PlanDefinition, PlanGrant } from './commerceApi';
import { getAdminEffectiveEntitlements, listPlanGrants, listPlans, requestCommerceOperation } from './commerceApi';
import { CommerceApprovalPanel } from './CommerceApprovalPanel';
import {
  DUAL_CONTROL_NOTE,
  ENTITLEMENT_FIELDS,
  canCommerceWrite,
  composeCommercialEntitlements,
  formatEntitlementValue,
  formatMinor,
  grantEffectiveState,
  grantEffectiveStateLabel,
  grantEffectiveStateTone,
  grantSourceLabel,
  grantStatusLabel,
  stackingLabel,
  subjectTypeLabel,
  usFromLocalInput,
} from './commerceModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

const SUBJECT_TYPES = ['account', 'device', 'device_group', 'org'] as const;
const GRANT_SOURCES = ['admin', 'support', 'promotion', 'partner', 'migration'] as const;
const STACKING_POLICIES = ['additive', 'max', 'replace'] as const;

export default function PlanGrantsPage() {
  const { role } = useAuth();
  const writable = canCommerceWrite(role);
  const [searchParams, setSearchParams] = useSearchParams();

  // 查詢軸:subject_key = type:id(後端 DDB Query 鍵,必填)
  const [queryType, setQueryType] = useState('account');
  const [queryId, setQueryId] = useState('');
  const [queriedKey, setQueriedKey] = useState('');
  const [grants, setGrants] = useState<PlanGrant[]>([]);
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [plansErr, setPlansErr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const [effective, setEffective] = useState<EffectiveEntitlementExplain | null>(null);
  const [effectiveErr, setEffectiveErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  const [latestOperationId, setLatestOperationId] = useState('');

  // 建立 grant 表單
  const [formSubjectType, setFormSubjectType] = useState('account');
  const [formSubjectId, setFormSubjectId] = useState('');
  const [formBillingAccount, setFormBillingAccount] = useState('');
  const [formPlanKey, setFormPlanKey] = useState('');
  const [formQuantity, setFormQuantity] = useState('1');
  const [formSource, setFormSource] = useState('admin');
  const [formStacking, setFormStacking] = useState('additive');
  const [formFrom, setFormFrom] = useState('');
  const [formTo, setFormTo] = useState(''); // 空 = 永久
  const [formFairValue, setFormFairValue] = useState('0');
  const [formCurrency, setFormCurrency] = useState('USD');
  const [formReason, setFormReason] = useState('');
  const [formCaseId, setFormCaseId] = useState('');
  const [confirmCreate, setConfirmCreate] = useState(false);

  // 撤銷
  const [revokeTarget, setRevokeTarget] = useState<PlanGrant | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeCaseId, setRevokeCaseId] = useState('');

  const publishedPlans = useMemo(() => plans.filter((p) => p.status === 'published'), [plans]);

  useEffect(() => {
    // plan catalog 供 grant 表單選版 + explain 展開;失敗不阻斷查詢(降級誠實)
    listPlans()
      .then(setPlans)
      .catch(() => setPlansErr(true));
    const subject = searchParams.get('subject');
    if (subject && subject.includes(':')) {
      const [t, ...rest] = subject.split(':');
      setQueryType(t);
      setQueryId(rest.join(':'));
      void runQuery(t, rest.join(':'), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runQuery(type: string, id: string, writeUrl = true) {
    const key = `${type}:${id.trim()}`;
    if (!id.trim()) return;
    setLoading(true);
    setLoadErr(null);
    setNotice('');
    setErr('');
    setEffective(null);
    setEffectiveErr(null);
    try {
      const [rows, explainResult] = await Promise.all([
        listPlanGrants(key),
        type === 'device' ? getAdminEffectiveEntitlements(id.trim()).then((value) => ({ value })).catch((error) => ({ error })) : Promise.resolve(null),
      ]);
      setGrants(rows);
      if (explainResult && 'value' in explainResult) setEffective(explainResult.value);
      if (explainResult && 'error' in explainResult) setEffectiveErr(explainResult.error);
      setQueriedKey(key);
      if (writeUrl) setSearchParams({ subject: key }, { replace: true });
    } catch (e) {
      setLoadErr(e);
      setQueriedKey(key);
      setGrants([]);
    } finally {
      setLoading(false);
    }
  }

  const nowUs = Date.now() * 1000;
  const activeSources = useMemo(
    () =>
      grants
        .filter((g) => grantEffectiveState(g, nowUs) === 'active')
        .map((g) => ({ grant: g, plan: plans.find((p) => p.plan_id === g.plan_id && p.version === g.plan_version) })),
    [grants, plans, nowUs]
  );
  const composed = useMemo(() => composeCommercialEntitlements(activeSources), [activeSources]);

  const selectedFormPlan = useMemo(
    () => publishedPlans.find((p) => `${p.plan_id}@${p.version}` === formPlanKey) ?? null,
    [publishedPlans, formPlanKey]
  );
  const fairValueMinor = Number.parseInt(formFairValue, 10);
  const quantity = Number.parseInt(formQuantity, 10);
  const windowValid = (() => {
    const from = usFromLocalInput(formFrom) ?? 0;
    const to = usFromLocalInput(formTo);
    return to === undefined || to > from;
  })();
  const createReady = Boolean(
    writable &&
      formSubjectId.trim() &&
      selectedFormPlan &&
      Number.isFinite(quantity) &&
      quantity >= 1 &&
      Number.isFinite(fairValueMinor) &&
      fairValueMinor >= 0 &&
      /^[A-Za-z]{3}$/.test(formCurrency.trim()) &&
      windowValid &&
      (formSubjectType === 'account' || formBillingAccount.trim()) &&
      formReason.trim() &&
      formCaseId.trim()
  );

  async function submitCreate() {
    if (!selectedFormPlan) return;
    setBusy(true);
    try {
      const payload = {
        billing_account_id: formBillingAccount.trim() || undefined,
        subject_type: formSubjectType,
        subject_id: formSubjectId.trim(),
        plan_id: selectedFormPlan.plan_id,
        plan_version: selectedFormPlan.version,
        quantity,
        source: formSource,
        stacking_policy: formStacking,
        effective_from: usFromLocalInput(formFrom) ?? 0,
        effective_to: usFromLocalInput(formTo) ?? 0,
        fair_value_minor: fairValueMinor,
        currency: formCurrency.trim().toUpperCase(),
      };
      const op = await requestCommerceOperation('grant.create', payload, {
        reason: formReason.trim(),
        case_id: formCaseId.trim(),
      });
      setConfirmCreate(false);
      setErr('');
      setLatestOperationId(op.operation_id);
      setNotice(`已建立 grant 覆核單 ${op.operation_id}；尚未異動權益或 ledger。`);
      setFormReason('');
      setFormCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRevoke() {
    if (!revokeTarget) return;
    setBusy(true);
    try {
      const op = await requestCommerceOperation('grant.revoke', {
        subject_type: revokeTarget.subject_type,
        subject_id: revokeTarget.subject_id,
        grant_id: revokeTarget.grant_id,
      }, {
        reason: revokeReason.trim(),
        case_id: revokeCaseId.trim(),
      });
      setLatestOperationId(op.operation_id);
      setNotice(`已建立撤銷覆核單 ${op.operation_id}；核准前 grant 仍有效。`);
      setRevokeTarget(null);
      setRevokeReason('');
      setRevokeCaseId('');
      setErr('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const activeCount = grants.filter((g) => g.status === 'active').length;

  return (
    <div className="commerce-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">權益贈與</h1>
          <p className="page-subtitle">客戶訂閱/權益來源鏈查詢與限期/永久 grant 治理;撤銷以反向分錄沖銷,全程稽核。</p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {err && !confirmCreate && !revokeTarget && (
        <p className="adm-action-result is-err" role="alert">
          {err}
        </p>
      )}

      <div className="commerce-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">依對象查詢贈與</h2>
              <span className="section-meta adm-mono">{queriedKey || '尚未查詢'}</span>
            </div>
            {loading && <span className="pill pill-neutral">查詢中</span>}
          </div>
          <div className="commerce-query-row" style={{ marginBottom: 'var(--spacing-6)' }}>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">對象類型</span>
              <select value={queryType} onChange={(e) => setQueryType(e.target.value)} disabled={loading}>
                {SUBJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {subjectTypeLabel(t)}({t})
                  </option>
                ))}
              </select>
            </label>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="adm-field-label text-secondary">對象 ID</span>
              <input
                value={queryId}
                onChange={(e) => setQueryId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void runQuery(queryType, queryId);
                }}
                placeholder={queryType === 'account' ? 'u-apac-…(user id)' : queryType === 'device' ? 'd-…(device id)' : 'id'}
                disabled={loading}
              />
            </label>
            <button className="btn-primary" disabled={loading || !queryId.trim()} onClick={() => void runQuery(queryType, queryId)}>
              查詢
            </button>
          </div>
          {loadErr ? (
            <ErrorState error={loadErr} onRetry={() => void runQuery(queryType, queryId)} />
          ) : queriedKey ? (
            grants.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>贈與</th>
                      <th>方案版本</th>
                      <th>數量</th>
                      <th>來源 / 疊加</th>
                      <th>生效期間</th>
                      <th>狀態</th>
                      {writable && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g) => {
                      const state = grantEffectiveState(g, nowUs);
                      return (
                        <tr key={g.grant_id}>
                          <td>
                            <div className="adm-mono" title={g.grant_id}>
                              {g.grant_id}
                            </div>
                          </td>
                          <td className="adm-mono">
                            {g.plan_id}@{g.plan_version}
                          </td>
                          <td>{g.quantity}</td>
                          <td>
                            {grantSourceLabel(g.source)}・{stackingLabel(g.stacking_policy)}
                          </td>
                          <td className="adm-mono">
                            {g.effective_from ? formatTimestampUs(g.effective_from, { second: undefined }) : '即刻'} →{' '}
                            {g.effective_to ? formatTimestampUs(g.effective_to, { second: undefined }) : '永久'}
                          </td>
                          <td>
                            <span className={`pill ${grantEffectiveStateTone(state)}`}>{grantEffectiveStateLabel(state)}</span>
                          </td>
                          {writable && (
                            <td>
                              {g.status === 'active' ? (
                                <button
                                  className="btn-ghost-danger btn-sm"
                                  disabled={busy}
                                  onClick={() => {
                                    setErr('');
                                    setRevokeTarget(g);
                                  }}
                                >
                                  撤銷
                                </button>
                              ) : (
                                <span className="text-tertiary">{grantStatusLabel(g.status)}</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🎁</div>
                <div className="empty-state-title">此對象目前沒有任何 grant</div>
                <div className="empty-state-detail">可在右側建立限期或永久 grant(reason / 案件編號必填)。</div>
              </div>
            )
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🔍</div>
              <div className="empty-state-title">輸入對象查詢權益來源</div>
              <div className="empty-state-detail">grant 以對象鍵(type:id)為查詢軸;帳號、裝置、裝置群組與組織皆可查。</div>
            </div>
          )}

          {queriedKey && !loadErr && (
            <>
              <div className="section-head" style={{ marginTop: 'var(--spacing-8)' }}>
                <div>
                  <h3 className="section-title">來源鏈 explain — {queriedKey}</h3>
                  <span className="section-meta">生效中 grant {activeCount > 0 ? `${activeSources.length} 筆` : '0 筆'} 的商業層展開</span>
                </div>
              </div>
              <div className="commerce-chain">
                {activeSources.length > 0 ? (
                  activeSources.map(({ grant, plan }) => (
                    <div className="commerce-chain-step" key={grant.grant_id}>
                      <span className="pill pill-info">方案／贈與</span>
                      <span className="adm-mono">
                        {grant.plan_id}@{grant.plan_version}
                      </span>
                      <span>
                        {plan ? plan.name : '(版本不在目錄回傳中)'}・{grantSourceLabel(grant.source)}・{stackingLabel(grant.stacking_policy)}・數量 {grant.quantity}
                      </span>
                      <span className="text-tertiary">
                        {grant.effective_to ? `至 ${formatTimestampUs(grant.effective_to, { second: undefined })}` : '永久'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="commerce-chain-step commerce-chain-step--muted">
                    <span className="pill pill-neutral">方案／贈與</span>
                    <span>無生效中 grant;此對象的商業權益僅來自其付費訂閱(訂閱在裝置詳情內檢視)。</span>
                  </div>
                )}
                {queryType === 'device' && effective ? (
                  <>
                    {effective.stages.map((stage) => (
                      <div className="commerce-chain-step" key={stage.layer}>
                        <span className={`pill ${stage.status === 'applied' ? 'pill-good' : stage.status === 'denied' ? 'pill-bad' : 'pill-warn'}`}>{stage.layer}</span>
                        <span>{stage.status}{stage.code ? ` · ${stage.code}` : ''}</span>
                      </div>
                    ))}
                    <div className="commerce-chain-step">
                      <span className={`pill ${effective.decision === 'effective' ? 'pill-good' : effective.decision === 'denied' ? 'pill-bad' : 'pill-warn'}`}>effective</span>
                      <span>{effective.decision} · snapshot <span className="adm-mono">{effective.snapshot_version}</span>{!effective.complete ? ' · 來源未齊，伺服器暫定結果' : ''}</span>
                    </div>
                  </>
                ) : queryType === 'device' && effectiveErr ? (
                  <ErrorState error={effectiveErr} onRetry={() => void runQuery(queryType, queryId)} />
                ) : (
                  <div className="commerce-chain-step commerce-chain-step--muted">
                    <span className="pill pill-neutral">effective explain</span>
                    <span>有效權益以裝置為 enforcement subject；將查詢類型切換為 device 可檢視 AID / runtime / policy 的伺服器判定。</span>
                  </div>
                )}
              </div>
              {effective && (
                <div className="commerce-ent-grid" style={{ marginTop: 'var(--spacing-5)' }}>
                  {ENTITLEMENT_FIELDS.map((def) => (
                    <div className="commerce-ent-item" key={`effective-${String(def.key)}`}>
                      <span className="commerce-ent-label">{def.label}</span>
                      <span className="commerce-ent-value">{formatEntitlementValue(def, effective.entitlements)}</span>
                    </div>
                  ))}
                </div>
              )}
              {composed && (
                <>
                  <div className="section-head" style={{ marginTop: 'var(--spacing-6)' }}>
                    <div>
                      <h3 className="section-title">商業層參考合成</h3>
                      <span className="section-meta">前端依 stacking policy 估算,僅供解讀;伺服端 resolver 為權威</span>
                    </div>
                  </div>
                  <div className="commerce-ent-grid">
                    {ENTITLEMENT_FIELDS.map((def) => (
                      <div className="commerce-ent-item" key={String(def.key)}>
                        <span className="commerce-ent-label">{def.label}</span>
                        <span className="commerce-ent-value">{formatEntitlementValue(def, composed)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </section>

        <aside className="commerce-side">
          <section className="card card--primary">
            <div className="section-head">
              <h2 className="section-title">{writable ? '建立 grant' : '唯讀檢視'}</h2>
            </div>
            {writable ? (
              <div className="commerce-form">
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">對象類型</span>
                    <select value={formSubjectType} onChange={(e) => setFormSubjectType(e.target.value)} disabled={busy}>
                      {SUBJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {subjectTypeLabel(t)}({t})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">對象 ID</span>
                    <input value={formSubjectId} onChange={(e) => setFormSubjectId(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">Billing Account{formSubjectType === 'account' ? '(帳號對象可留空,自動開戶)' : ''}</span>
                  <input value={formBillingAccount} onChange={(e) => setFormBillingAccount(e.target.value)} placeholder="ba-…" disabled={busy} />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">方案版本(僅可授已發布版本)</span>
                  <select value={formPlanKey} onChange={(e) => setFormPlanKey(e.target.value)} disabled={busy}>
                    <option value="">選擇已發布版本</option>
                    {publishedPlans.map((p) => (
                      <option key={`${p.plan_id}@${p.version}`} value={`${p.plan_id}@${p.version}`}>
                        {p.plan_id}@{p.version} — {p.name}
                      </option>
                    ))}
                  </select>
                  {plansErr && <span className="commerce-hint text-tertiary">方案目錄載入失敗,請重新整理後再試。</span>}
                </label>
                <div className="commerce-form-grid commerce-form-grid--3">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">數量</span>
                    <input value={formQuantity} onChange={(e) => setFormQuantity(e.target.value)} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">來源</span>
                    <select value={formSource} onChange={(e) => setFormSource(e.target.value)} disabled={busy}>
                      {GRANT_SOURCES.map((s) => (
                        <option key={s} value={s}>
                          {grantSourceLabel(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">疊加策略</span>
                    <select value={formStacking} onChange={(e) => setFormStacking(e.target.value)} disabled={busy}>
                      {STACKING_POLICIES.map((s) => (
                        <option key={s} value={s}>
                          {stackingLabel(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">生效自(留空=即刻)</span>
                    <input type="datetime-local" value={formFrom} onChange={(e) => setFormFrom(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">生效至(留空=永久)</span>
                    <input type="datetime-local" value={formTo} onChange={(e) => setFormTo(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">公允價值(最小幣別單位,入帳用)</span>
                    <input value={formFairValue} onChange={(e) => setFormFairValue(e.target.value)} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">幣別(需同 billing account)</span>
                    <input value={formCurrency} onChange={(e) => setFormCurrency(e.target.value)} placeholder="USD" disabled={busy} />
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">原因</span>
                    <input value={formReason} onChange={(e) => setFormReason(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">案件編號</span>
                    <input value={formCaseId} onChange={(e) => setFormCaseId(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <p className="commerce-dc-note">{DUAL_CONTROL_NOTE}</p>
                <button className="btn-primary" disabled={busy || !createReady} onClick={() => { setErr(''); setConfirmCreate(true); }}>
                  建立 grant
                </button>
                {!createReady && (
                  <span className="commerce-hint text-tertiary">
                    需填妥對象、已發布方案版本、數量(≥1)、公允價值/幣別、原因與案件編號{!windowValid ? ';生效窗迄時需晚於起時' : ''}
                    {formSubjectType !== 'account' ? ';非帳號對象需明填 billing account' : ''}。
                  </span>
                )}
              </div>
            ) : (
              <p className="commerce-hint text-tertiary">目前角色僅能檢視權益來源;grant 建立/撤銷由 admin / platform-owner 執行。</p>
            )}
          </section>
        </aside>
      </div>

      <CommerceApprovalPanel
        operationId={latestOperationId}
        onCompleted={() => {
          if (queriedKey) void runQuery(queryType, queryId);
        }}
      />

      {confirmCreate && selectedFormPlan && (
        <ConfirmDialog
          title="建立 Plan grant"
          tone="primary"
          confirmLabel="確認建立"
          busy={busy}
          error={err || undefined}
          warning={DUAL_CONTROL_NOTE}
          body={
            <>
              <p>
                將對 <span className="adm-mono">{formSubjectType}:{formSubjectId.trim()}</span> 授予{' '}
                <span className="adm-mono">
                  {selectedFormPlan.plan_id}@{selectedFormPlan.version}
                </span>
                ({selectedFormPlan.name})×{formQuantity},{formTo ? `限期至 ${formTo.replace('T', ' ')}` : '永久'};帳面公允價值{' '}
                {formatMinor(fairValueMinor, formCurrency)} 將以平衡分錄入 ledger。
              </p>
              <p className="text-tertiary">處置理由:{formReason.trim()}・案件:{formCaseId.trim()}(將寫入稽核與 權益日誌)</p>
            </>
          }
          onConfirm={() => void submitCreate()}
          onCancel={() => setConfirmCreate(false)}
        />
      )}

      {revokeTarget && (
        <ConfirmDialog
          title="撤銷 Plan grant"
          tone="danger"
          confirmLabel="確認撤銷"
          busy={busy}
          confirmDisabled={!revokeReason.trim() || !revokeCaseId.trim()}
          error={err || undefined}
          warning="撤銷即時生效:對象立即失去此 grant 的權益;ledger 以反向分錄沖銷,不會刪除歷史。"
          body={
            <>
              <p>
                將撤銷 <span className="adm-mono">{revokeTarget.grant_id}</span>(
                <span className="adm-mono">
                  {revokeTarget.plan_id}@{revokeTarget.plan_version}
                </span>
                ,對象 <span className="adm-mono">{revokeTarget.subject_type}:{revokeTarget.subject_id}</span>)。
              </p>
              <label className="adm-field">
                <span className="adm-field-label text-secondary">原因(必填)</span>
                <input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} disabled={busy} />
              </label>
              <label className="adm-field">
                <span className="adm-field-label text-secondary">案件編號(必填)</span>
                <input value={revokeCaseId} onChange={(e) => setRevokeCaseId(e.target.value)} disabled={busy} />
              </label>
              <p className="text-tertiary" style={{ margin: 0 }}>
                撤銷將寫入稽核與 權益日誌。
              </p>
            </>
          }
          onConfirm={() => void submitRevoke()}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}
